import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { getAttribution } from "@/lib/attribution";
import { Seo } from "@/components/Seo";
import { Leaf, CheckCircle2, Clock, ArrowRight, Loader2 } from "lucide-react";

const FORMS = [
  "Multi-Point Inspection",
  "Repair Order / Write-Up Checklist",
  "Customer Vehicle Condition & Walk-Around",
  "Declined Services Log",
  "Technician Time & Flag Sheet",
  "Quality-Control (Final) Checklist",
  "Post-Service Customer Follow-Up",
];

export default function FallPreview() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true); setErr("");
    try {
      await api.post("/promo/fall-preview", { email });
      setDone(true);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="fall-preview-page">
      <Seo
        title="Fall Preview — 7 Free Service Department Forms | Centennial"
        description="Grab the 7 forms every service manager should standardize first — free this season. Seasons change; so should the 'we've always done it this way' habits."
        canonicalPath="/fall-preview"
      />
      {/* HERO */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-[1080px] mx-auto px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-amber-400 font-semibold">
              <Leaf className="w-4 h-4" /> Fall Preview · End-of-summer promo
            </span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-4 leading-[1.05]">
              Seasons change. So should "we've always done it this way."
            </h1>
            <p className="text-white/75 text-lg mt-5 leading-relaxed">
              Get the <strong className="text-white">7 forms every service manager should standardize first</strong> —
              free, straight to your inbox. A limited end-of-summer Fall Preview to help you head into the busy season
              with a system, not a scramble.
            </p>
            <div className="inline-flex items-center gap-2 mt-6 text-amber-300 text-sm font-semibold">
              <Clock className="w-4 h-4" /> Limited time — grab it while the Fall Preview is live
            </div>
          </div>

          {/* CAPTURE CARD */}
          <div className="bg-white text-foreground p-8 rounded-sm shadow-2xl" data-testid="fall-preview-card">
            {done ? (
              <div className="text-center py-6" data-testid="fall-preview-success">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto" />
                <h2 className="font-heading font-black text-2xl mt-4">Check your inbox! 🍂</h2>
                <p className="text-muted-foreground mt-2">Your Fall Preview and the 7 forms are on the way. See you in the busy season.</p>
                <Link to="/forms" className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 font-semibold mt-6 btn-lift">
                  Explore the forms now <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <>
                <h2 className="font-heading font-black text-2xl">Send me the 7 forms — free</h2>
                <p className="text-muted-foreground text-sm mt-2">One email. No card. Instant Fall Preview access.</p>
                <form onSubmit={submit} className="mt-5 space-y-3">
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@yourdealership.com" data-testid="fall-preview-email"
                    className="w-full border border-border px-4 py-3.5 text-base focus:outline-none focus:border-primary" />
                  <button type="submit" disabled={busy} data-testid="fall-preview-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white py-3.5 font-semibold btn-lift disabled:opacity-60">
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Get my Fall Preview <ArrowRight className="w-4 h-4" /></>}
                  </button>
                  {err && <p className="text-sm text-red-600" data-testid="fall-preview-error">{err}</p>}
                </form>
                <p className="text-xs text-muted-foreground mt-3">We'll also send occasional practical tips. Unsubscribe anytime.</p>
              </>
            )}
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="max-w-[1080px] mx-auto px-6 lg:px-8 py-16">
        <h2 className="font-heading font-black text-3xl tracking-tight">What you'll get</h2>
        <p className="text-muted-foreground mt-2">The exact forms that bring order to your service drive — built on Process → Procedure → Reference.</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-8">
          {FORMS.map((f, i) => (
            <div key={f} className="flex items-center gap-3 border border-border bg-white p-5" data-testid={`fall-form-${i}`}>
              <span className="font-heading font-black text-primary text-lg w-7">{i + 1}</span>
              <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
              <span className="font-medium">{f}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
